import { OnboardingStatusToCurrentStateMap } from "client/domain/OnboardingStatus/types";
import {
  CountryList,
  OnboardingGroup,
  OnboardingStatus,
  OpportunityStage,
  SalesChannel,
} from "client/lib/apt-merchant";
import { Branding } from "client/utils/branding";
import hasInstoreChannel from "client/utils/hasInstoreChannel";

export type OnboardingStep =
  | "ActivateInStore"
  | "AddCashPay"
  | "Apply"
  | "Complete"
  | "Compliance"
  | "DirectDebit"
  | "InStoreTraining"
  | "Integration"
  | "CashPayProfileStatus"
  | "CashPayIntegration"
  | "CustomizeCashPayProfile"
  | "Messaging";

export type OnboardingStepStatusMap = {
  [key in OnboardingStep]: OnboardingStatusToCurrentStateMap;
};

type SidebarHeadings = {
  onboardingStep: OnboardingStep;
  heading?: string;
  subheading?: string;
};

type SidebarStatus = {
  currentlyPolling: boolean;
  inStoreDirectDebitAuthorized: boolean;
  inStoreTrainingComplete?: boolean;
  messagingComplete: boolean;
  onboardingGroup?: OnboardingGroup;
  onboardingStatus?: OnboardingStatus;
  opportunityStage?: OpportunityStage;
  registeredCountry: CountryList;
  salesChannel?: SalesChannel;
  trainingButtonClicked?: boolean;
};

const IntegrationSidebarHeadings: SidebarHeadings = {
  heading: "uiStatusIntegrationHeading",
  onboardingStep: "Integration",
  subheading: "uiStatusIntegrationSubheading",
};

const CompleteSidebarHeadings: SidebarHeadings = {
  heading: "uiStatusCompleted",
  onboardingStep: "Complete",
};

const DirectDebitHeadings: SidebarHeadings = {
  heading: "uiStatusTraingCompleteHeading",
  onboardingStep: "DirectDebit",
};

export const getSidebarHeadings = ({
  currentlyPolling,
  inStoreDirectDebitAuthorized,
  inStoreTrainingComplete,
  messagingComplete,
  onboardingGroup,
  onboardingStatus,
  opportunityStage,
  salesChannel,
  trainingButtonClicked,
  registeredCountry,
}: SidebarStatus): SidebarHeadings => {
  if (currentlyPolling) {
    return {
      heading: "uiStatusCheckingDetailsHeading",
      onboardingStep: "Integration",
      subheading: "uiStatusCheckingDetailsSubheading",
    };
  }
  switch (onboardingStatus) {
    case "IN_REVIEW":
      if (
        onboardingGroup === "ZDO Merchant" ||
        onboardingGroup === "ZDO Pilot V1"
      ) {
        return {
          heading: "uiStatusIntegrationZdoHeading",
          onboardingStep: "Integration",
        };
      }
      if (hasInstoreChannel(salesChannel)) {
        // uk instore does not require training to be complete and does not have direct debit, so we bypass those
        if (registeredCountry === "United Kingdom") {
          return {
            heading: "uiStatusInReviewHeading",
            onboardingStep: "Compliance",
            subheading: "uiStatusInReviewSubheading",
          };
        }
        // Right now the two steps that a merchant can complete before
        // compliance review is complete for in-store is direct debit and
        // the in-store training. If both of these are complete, wait for
        // complaince to finish before showing other steps
        if (inStoreDirectDebitAuthorized && inStoreTrainingComplete) {
          return {
            heading: "uiStatusInReviewDirectDebitCompleteHeading",
            onboardingStep: "Compliance",
            subheading: "",
          };
        }
        if (inStoreTrainingComplete) {
          return DirectDebitHeadings;
        }

        if (trainingButtonClicked) {
          return {
            heading: "uiStatusInStorePostTrainingInReviewHeading",
            onboardingStep: "InStoreTraining",
          };
        }

        return {
          heading: "uiStatusInReviewHeading",
          onboardingStep: "InStoreTraining",
        };
      }
      return {
        heading: "uiStatusInReviewHeading",
        onboardingStep: "Compliance",
        subheading: "uiStatusInReviewSubheading",
      };
    case "REQUIRES_ATTENTION":
      return {
        onboardingStep: "Compliance",
        heading: "uiStatusNeedsAttentionHeading",
      };
    case "READY_TO_INSTALL":
      if (
        salesChannel === "In-store Only" ||
        salesChannel === "Online and In-store"
      ) {
        if (!inStoreTrainingComplete || !inStoreDirectDebitAuthorized) {
          if (inStoreTrainingComplete) {
            return DirectDebitHeadings;
          }

          if (trainingButtonClicked) {
            return {
              heading: "uiStatusInStorePostTrainingInReviewHeading",
              onboardingStep: "InStoreTraining",
            };
          }

          return {
            heading: "uiStatusInReviewHeading",
            onboardingStep: "InStoreTraining",
          };
        }

        if (
          opportunityStage !== "Closed Won" &&
          opportunityStage !== "Implemented"
        ) {
          return {
            heading: "uiStatusInStoreActivateInStoreSidebarHeading",
            onboardingStep: "ActivateInStore",
            subheading: "",
          };
        }
      }

      return IntegrationSidebarHeadings;

    case "COMPLETE":
      if (messagingComplete) {
        return CompleteSidebarHeadings;
      }
      return {
        heading: "uiStatusMessagingHeading",
        onboardingStep: "Messaging",
      };

    case "INELIGIBLE":
      return {
        heading: "uiStatusDeclinedSidebarHeading",
        onboardingStep: "Compliance",
      };
    default:
      return {
        onboardingStep: "Apply",
      };
  }
};

export const onboardingStatusStepToHeading: Record<OnboardingStep, string> = {
  ActivateInStore: "uiStatusInStoreActivateInStoreSidebarHoverHeading",
  Messaging: "uiStatusMessagingHeading",
  Apply: "",
  AddCashPay: "uiStatusAddCashPayHoverHeading",
  CashPayIntegration: "uiStatusCashPayIntegrationHoverHeading",
  CashPayProfileStatus: "uiStatusCashPayIntegrationHoverHeading",
  CustomizeCashPayProfile: "uiStatusCustomizeCashPayProfileHoverHeading",
  Compliance: "uiStatusInReviewHeading",
  Complete: "",
  DirectDebit: "uiStatusTraingCompleteHeading",
  InStoreTraining: "uiStatusInStorePostTrainingInReviewHeading",
  Integration: "uiStatusIntegrationHeading",
};

export const onboardingStatusStepToTargetedSubHeading: Record<
  OnboardingStep,
  string
> = {
  ActivateInStore: "",
  Messaging: "",
  Apply: "",
  AddCashPay: "uiStatusAddCashPayHoverSubheading1",
  CashPayIntegration: "uiStatusCashPayIntegrationHoverSubheading1",
  CashPayProfileStatus: "",
  CustomizeCashPayProfile: "uiStatusCustomizeCashPayProfileHoverSubheading",
  Compliance: "",
  Complete: "",
  DirectDebit: "",
  InStoreTraining: "",
  Integration: "uiStatusIntegrationSubheading",
};

export const isZdo = (onboardingGroup: OnboardingGroup): boolean =>
  onboardingGroup === "ZDO Merchant" || onboardingGroup === "ZDO Pilot V1";

// Afterpay (New)
// https://{myshopify domain or general Shopify base URL}/admin/settings/payments/alternative-providers/1057655
// Clearpay (New)
// https://{myshopify domain or general Shopify base URL}/admin/settings/payments/alternative-providers/1057657
export const SHOPIFY_PAYMENTS_PAGE = `https://accounts.shopify.com/store-login?redirect=settings%2Fpayments/alternative-providers`;
export const CASH_SHOPIFY_LINK = "https://apps.shopify.com/cash-app-pay";

export const PaymentProviderId: Record<Branding, string> = {
  Afterpay: "1057655",
  CashApp: "84934657",
  Clearpay: "1057657",
};

// TODO: the following function has been modified with a temporary solution to handle
// linking to the new Shopify App while we are migrating to the new Shopify App
// but before the $Afterpay brand is live.
// Revert this to the original logic once the $Afterpay brand is live. Code to revert to:
// https://github.com/AfterpayTouch/merchant-portal/blob/3e2763c73c9f0ecaf80c94366b3679301c124661/src/client/pages/OnboardingStatus/utils.ts#L248-L249
export const getAfterpayShopifyLink = (
  brandTitle: Branding,
  registeredCountry: CountryList | undefined
): string => {
  const brand = registeredCountry === "United States" ? "CashApp" : brandTitle;
  const providerId = PaymentProviderId[brand];

  return `${SHOPIFY_PAYMENTS_PAGE}/${providerId}`;
};
